import { get } from '@/utils/Api'

import { getThirdLevelDomain } from '@/utils/Helpers'
import Vue from 'vue'
import Vuex from 'vuex'
import auth from './modules/auth'
import building from './modules/building'
import settings from './modules/settings'
import employee from './modules/employee'
import floor from './modules/floor'
import point from './modules/point'
import pointIcon from './modules/pointIcon'
import room from './modules/room'
import printer from './modules/printer'
import pc from './modules/pc'
import map from './modules/map'
import position from './modules/position'
import department from './modules/department'
import user from './modules/user'
import desk from './modules/desk'
import rbac from './modules/rbac'

Vue.use(Vuex)

export const ROOT_UPDATE = 'ROOT_UPDATE'
export const MODAL_TOGGLE_STATE = 'MODAL_TOGGLE_STATE'

export const SIDENAV_CLASSES = {
  collapsed: 'layout-collapsed',
  disabled: 'layout-sidenav-disabled',
  expanded: 'layout-expanded'
}

const retry = (asyncFn, attempts, delay) => {
  let count = 0
  let step = async (resolve, reject) => {
    setTimeout(() => {
      let result = asyncFn()
      if (result) {
        resolve(result)
      } else if (attempts > count) {
        step(resolve, reject)
      }
    }, count++ * delay)
  }

  return new Promise((resolve, reject) => {
    step(resolve, reject)
  })
}

/**
 * Add/remove collapsed css class depends on state
 * @param store
 */
const sidenavClassPlugin = (store) => {
  store.subscribe((mutation, state) => {
    if (typeof state.sidenavClass !== 'undefined') {
      // clear prev classes
      Object.keys(SIDENAV_CLASSES).forEach((key) => {
        document.documentElement.classList.remove(SIDENAV_CLASSES[key])
      })
      if (state.sidenavClass) {
        document.documentElement.classList.add(state.sidenavClass)
      }
    }
  })
}

let iframeLoadPromise

const store = new Vuex.Store({
  state: {
    sidenavClass: null,
    workspaceStatus: {},
    isCommonDataLoaded: false,
    modalActiveCount: 0 // should be int to count all opened modals
  },
  getters: {
    workspaceDomain() {
      return getThirdLevelDomain(window.location)
    },
    frontDomain() {
      let hostParts = window.location.hostname.split('.')
      if (hostParts.length > 1) {
        return hostParts.slice(-2).join('.')
      }
      throw new Error('Unknown host format')
    }
  },
  mutations: {
    [ROOT_UPDATE](state, payload) {
      for (let name of Object.keys(payload)) {
        state[name] = payload[name]
      }
    },
    [MODAL_TOGGLE_STATE](state, active) {
      if (active) {
        state.modalActiveCount += 1
      } else {
        state.modalActiveCount -= 1
      }
    }
  },
  actions: {
    getLocalStorageWindow({ getters, commit }) {
      if (!iframeLoadPromise) {
        iframeLoadPromise = new Promise((resolve, reject) => {
          const url = `https://${getters.frontDomain}/iframe-bus.html?origin=${window.location.origin}`
          let iframe = document.createElement('iframe')
          iframe.onload = () => {
            resolve(iframe.contentWindow)
            commit(ROOT_UPDATE, { iframe })
          }
          iframe.setAttribute('src', url)
          iframe.style.width = '1px'
          iframe.style.height = '1px'
          iframe.style.position = 'absolute'
          iframe.style.left = '-100vw'
          iframe.style['z-index'] = '-10'
          document.body.appendChild(iframe)
        })
      }

      return iframeLoadPromise
    },
    setSidenavClass({ state, commit }, sidenavClass = '') {
      commit(ROOT_UPDATE, { sidenavClass })
    },
    loadWorkspaceStatus({ getters, state, commit }) {
      if (state.workspaceStatus && state.workspaceStatus.status === 'ok') {
        return Promise.resolve(true)
      }

      if (getters.workspaceDomain.length > 0) {
        const checker = async () =>
          get('/auth/workspace').then(() => {
              commit(ROOT_UPDATE, { workspaceStatus: { status: 'ok' } })
              return state.workspaceStatus
            }, (err) => {
              if (err.response && err.response.status === 423) {
                commit(ROOT_UPDATE, { workspaceStatus: { status: 'error', errors: err.response.data.errors } })
                return state.workspaceStatus
              }
              throw err
            })

        return retry(checker, 10, 1000)
      }

      return Promise.resolve(false)
    },
    loadCommonData({ state, commit, dispatch, rootState }) {
      if (state.isCommonDataLoaded) {
        return Promise.resolve()
      }

      return new Promise((resolve, reject) => {
        if (state.workspaceStatus.status === 'ok' && rootState.auth.user) {
          // add new load actions here
          dispatch('floor/floorsLoad', { params: { sort: 'building_id,-sort' } })
          dispatch('building/buildingsLoad', { params: { sort: 'name' } })
          dispatch('department/departmentsLoad', { params: { sort: 'name' } })
          dispatch('position/positionsLoad', { params: { sort: 'name' } })
          dispatch('pointIcon/pointIconsLoad')
          dispatch('settings/loadAll')

          commit(ROOT_UPDATE, { isCommonDataLoaded: false })
          resolve(true)
        } else {
          reject(new Error('Common data load not started yet!'))
        }
      })
    }
  },
  modules: {
    auth,
    settings,
    building,
    floor,
    point,
    pointIcon,
    employee,
    room,
    printer,
    department,
    position,
    pc,
    map,
    user,
    desk,
    rbac
  },
  plugins: [sidenavClassPlugin]
})

/**
 * Body scroll show/hide on modal activate
 * @type {string}
 */
const MODAL_ACTIVE_CLASS = 'modal-active'
store.subscribe((mutation, state) => {
  if (mutation.type === MODAL_TOGGLE_STATE) {
    if (state.modalActiveCount) {
      document.body.classList.add(MODAL_ACTIVE_CLASS)
    } else {
      document.body.classList.remove(MODAL_ACTIVE_CLASS)
    }
  }
})

export default store
